import React, { useEffect, useMemo, useState } from 'react';
import { Link, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { useIntl } from '@ysoft/react-intl';
import { ErrorMessageBar } from '../../general-components/ErrorMessageBar';
import { generateLoading } from '../../libs/generateLoading';
import Configuration from './Configuration';
import { mergeStyleSets } from '@fluentui/react';
import { useApiContext } from '../../libs/apiContext';
import useSWR, { SWRConfiguration } from 'swr';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfigContext } from '../../config/configContext';
import { Api } from '../../../types/api';

type PortConfigurationParams = {
	id: string;
};

export const DeviceDetailPage: React.FC = () => {
	const { t } = useIntl();
	const api = useApiContext();
	const [longLoading, setLongLoading] = useState(false);
	const { id = '' } = useParams<PortConfigurationParams>();
	const navigate = useNavigate();
	const { config } = useConfigContext();

	const stackClasses = mergeStyleSets({
		formContainer: {
			minHeight: '90vh',
		},
	});

	const fetcher = (url: string) => api.apiInstance.get(url).then((res) => res.data);

	const swrOptions: SWRConfiguration = {
		revalidateOnFocus: false,
		loadingTimeout: 2000,
		onLoadingSlow: () => setLongLoading(true),
	};

	const {
		data: moduleTwin,
		error: moduleTwinError,
		mutate: mutateModuleTwin,
	} = useSWR<Api.ModuleTwin>(`/configuration/${id}`, fetcher, swrOptions);

	const licenseFetcher = (
		url: string
	): Promise<Api.License | Record<string, never>> =>
		config.licenseToggle
			? api.apiInstance.get(url).then((res) => res.data)
			: new Promise((resolve) => {
					resolve({});
			  });
	const {
		data: license,
		error: licenseError,
		mutate: mutateValidLicense,
	} = useSWR<Api.License | Record<string, never>>(
		`/Licenses/ValidDeviceLicense/${id}`,
		licenseFetcher,
		{
			...swrOptions,
			dedupingInterval: 0,
		}
	);

	useEffect(() => {
		setLongLoading(false);
	}, [moduleTwinError, moduleTwin]);

	const hasUnsupportedLicense = (): boolean => {
		return (license && license.type === 'Unknown') ?? false;
	};

	const back = () => {
		navigate('/devices');
	};

	const breadcrumbStyles = useMemo(
		() =>
			mergeStyleSets({
				container: {
					minHeight: 32,
					fontSize: 20,
					lineHeight: '20px',
				},
				link: {
					color: '#0077d4',
					display: 'inline-block',
					padding: '4px 8px',
					borderRadius: 2,
					textDecoration: 'none',
					selectors: {
						':hover': {
							backgroundColor: '#e9f6f9',
							color: '#0000ee',
							textDecoration: 'underline',
						},
						':hover:focus, :focus-visible': {
							backgroundColor: '#e9f6f9',
							color: '#0000ee',
							textDecoration: 'underline',
						},
					},
				},
				separator: {
					margin: '0 4px',
					padding: '0 0 5px',
					color: '#605e5c',
					fontSize: 30,
					userSelect: 'none',
				},
				idText: {
					color: '#323130',
					fontSize: 20,
					lineHeight: '20px',
					fontWeight: 600,
				},
			}),
		[]
	);

	return moduleTwinError ? (
		<ErrorMessageBar
			data-testid="port-configuration__error-message-bar"
			error={moduleTwinError?.response ?? licenseError?.response}
		/>
	) : moduleTwin && (license || licenseError) ? (
		license && hasUnsupportedLicense() ? (
			<MessageBar
				messageBarType={MessageBarType.error}
				data-testid="port-configuration__error-message-bar"
			>
				{t('port-configuration__unsupported-license')}
			</MessageBar>
		) : (
			<>
				<Stack
					data-testid="port-configuration"
					className={stackClasses.formContainer}
					tokens={{ childrenGap: 8 }}
				>
					<Stack.Item>
						<Stack
							horizontal
							verticalAlign="center"
							tokens={{ childrenGap: 4 }}
							data-testid="port-configuration__breadcrumb"
							className={breadcrumbStyles.container}
						>
							<Link className={breadcrumbStyles.link} onClick={back}>
								{t('port-configuration__breadcrumb-text')}
							</Link>
							<span className={breadcrumbStyles.separator}>&rsaquo;</span>
							<span className={breadcrumbStyles.idText}>{id}</span>
						</Stack>
					</Stack.Item>
					<Configuration
						deviceId={id}
						back={back}
						license={license}
						licenseError={licenseError}
						mutateValidLicense={mutateValidLicense}
						moduleTwin={moduleTwin}
						mutateModuleTwin={mutateModuleTwin}
					/>
				</Stack>
			</>
		)
	) : longLoading ? (
		<Spinner
			size={SpinnerSize.large}
			label={t('port-configuration__spinner-label')}
		/>
	) : (
		generateLoading()
	);
};
